import { NtaPatient, Role } from '@ss/typings/nta-hunter';
import { FieldValue } from 'firebase/firestore';

export type FacilityAppRoute = 'simplimds' | 'simpliadr' | 'simplirounds' | 'settings';

export class UserNotificationSetting {
	userId?: string;

	type? = '';

	emailEnabled? = false;

	webEnabled? = true;

	description? = '';

	timing?: 'weekly' | 'monthly' | 'never';

	enterpriseOnly? = false;

	title?: string;

	constructor(partial: Partial<UserNotificationSetting>) {
		Object.assign(this, partial);
	}
}

export class SimpliscreenUser {
	currentFacilityId: string;

	lastFacilityAppRoute?: FacilityAppRoute;

	facilityIds: string[];

	enterpriseId: string;

	enterpriseIds: string[];

	userId: string;

	facilities: Array<Partial<Facility>>;

	facility: Partial<Facility>;

	email: string;

	phoneNumber: string;

	name: string;

	isSuperAdmin = false;

	corpName: string;

	corporationId: string;

	claimsUpdatedAt?: Date | FieldValue;

	recentNtaPdfs: string[] = [];

	hasTempPassword = false;

	mfaEnabled = false;

	createdBy?: string;

	createdDate?: Date | FieldValue;

	notificationSettings: UserNotificationSetting[];

	pccUserPermissions?: string[];

	constructor(partial?: Partial<SimpliscreenUser>) {
		Object.assign(this, partial);
	}
}

export class UserClaims {
	isSuperAdmin = false;

	corporationIds: string[] = [];

	enterpriseIds: string[] = [];

	facilities: Record<string, Role> = {};

	constructor(partial?: Partial<UserClaims>) {
		Object.assign(this, partial);
	}
}

export class NtaUser {
	userId: string;

	email: string;

	name: string;

	role: Role;
}

export class Corporation {
	ntaUsers: Record<string, NtaUser> = {};

	enterpriseIds: string[] = [];

	corporationId: string;

	constructor(partial:Partial<Corporation>) {
		Object.assign(this, partial);
	}
}

export class Enterprise {

	enterpriseId: string;

	name?: string;

	facilityIds: string[] = [];

	ntaUsers: Record<string, NtaUser> = {};

	fakeStats = false;

	isCorporate = false;

	facilityType: string;

	pccOrgUuid?: string;

	corporationId?: string;

	constructor(partial?: Partial<Enterprise>) {
		Object.assign(this, partial);
	}
}

export class Facility {
	billingActive = true;

	name:string;

	enterpriseId: string;

	corporationId?: string;

	patientCount: number;

	monthlyPatientCount: number;

	createdDate: Date;

	facilityId: string;

	facilityType: string;

	ntaEnabled = false;

	ntaEnabledYN?: 'Yes' | 'No' = 'No';

	stripe:{
		customer:string;

		subscription?: string;
	};

	ntaUsers:NtaUser[] = [];

	recentlyViewedPatients: Array<Partial<NtaPatient>> = [];

	recentlyViewedAdrPatients: Array<Partial<NtaPatient>> = [];

	currentUserIsAdmin = false;

	features: string[] = [];

	addressLine1: string;

	addressLine2: string;

	city: string;

	state: {
		abbreviation: string;

		name: string;
	};

	zip: string;

	pccFacilityId?: number;

	pccOrgUuid?: string;

	pendingPccFacilityId?: number;

	pendingPccOrgUuid?: string;

	pccTimeZone?: string;

	pccLayoutSyncedAt?: Date;

	pccLayoutSyncInProgress?: boolean;

	pccLayoutSyncStartedAt?: Date;

	pccLayoutSyncError?: string | null;

	get address() {
		return `${this.addressLine1}\n${this.addressLine2}${this.addressLine2 ? '\n' : ''}${this.city}, ${this.state.abbreviation} ${this.zip}`;
	}

	get ntaUserNames() {
		return this.ntaUsers.slice().sort((a, b) => {
			if (a.role === 'admin' && b.role !== 'admin') return -1;
			if (a.role !== 'admin' && b.role === 'admin') return 1;
			return a.name.localeCompare(b.name);

		}).map((user) => user.name);
	}

	get isPccReadyToLink(): boolean {
		return !!this.pendingPccFacilityId && !!this.pendingPccOrgUuid && !this.pccFacilityId;
	}

	get pccLinkDisplayStatus(): '' | 'Ready' | 'Linked' {
		if (this.pccFacilityId) return 'Linked';
		if (this.isPccReadyToLink) return 'Ready';
		return '';
	}

	constructor(partial?:Partial<Facility>, initAll = true) {
		Object.assign(this, partial);

		if (initAll) {

			if (!Array.isArray(this.ntaUsers)) {
				this.ntaUsers = Object.keys(this.ntaUsers as any ?? {}).map((userId) => ({
					userId,
					...this.ntaUsers[userId]
				}));
			}
		}

		this.ntaEnabledYN = this.ntaEnabled ? 'Yes' : 'No';

		const syncedAt = partial?.pccLayoutSyncedAt as any;

		if (typeof syncedAt?.toDate === 'function') {
			this.pccLayoutSyncedAt = syncedAt.toDate();
		}

		const syncStartedAt = partial?.pccLayoutSyncStartedAt as any;
		if (typeof syncStartedAt?.toDate === 'function') {
			this.pccLayoutSyncStartedAt = syncStartedAt.toDate();
		}
	}
}
